import React, { useState, useEffect } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface EditEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialEmail: string;
    onSave: (newEmail: string) => Promise<void>;
    isSaving?: boolean;
    /** Which address is being written - "Patient Email" or "Parent1 Email".
     *  Named explicitly because get_email reads the parent's address for any
     *  non-Adult patient, so a single unlabelled box is ambiguous. */
    fieldLabel?: string;
}

export const EditEmailModal: React.FC<EditEmailModalProps> = ({
    isOpen,
    onClose,
    initialEmail,
    onSave,
    isSaving = false,
    fieldLabel = 'Patient Email',
}) => {
    const [email, setEmail] = useState(initialEmail);
    const [error, setError] = useState<string | null>(null);

    // Update local state when initialEmail changes
    useEffect(() => {
        setEmail(initialEmail || '');
    }, [initialEmail]);

    if (!isOpen) return null;

    const handleSave = async () => {
        try {
            setError(null);
            await onSave(email);
            // onClose will be called by parent after successful save
        } catch (err: any) {
            setError(err.message || 'Failed to save email');
        }
    };

    const handleCancel = () => {
        setEmail(initialEmail || '');
        setError(null);
        onClose();
    };

    return (
        <ModalWrapper modalId="EditEmailModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Edit Email"
            isLoading={isSaving}
        >
            {/* Email Input */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {fieldLabel}
                </label>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={POPUP_STYLES.input}
                    placeholder="test@example.com"
                    disabled={isSaving}
                />
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Buttons */}
            <div className="flex justify-center gap-4">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
